import { PublishingEntity } from 'client/respondent/core/types';
import { MaterialIcon, MaterialIconName } from 'client/shared/components/base';
import { Tooltip } from 'client/shared/components/tooltip';
import React from 'react';
import { IconButton, Stack } from '@mui/material';
import { MutationInfos } from 'client/shared/graphql-mutations';
import { useMutationInfo } from 'client/shared/containers/mutation';
import { QueryInfos } from 'client/shared/graphql-queries';
import { UnfollowConfirm } from 'client/respondent/feed/components';
import { FollowPublisherAccountPrompt } from 'client/respondent/shared/components/follow-publisher-account-prompt';
import { fbShare, twitterShare } from 'client/shared/integrations';
import { CurrentRespondentData } from 'client/respondent/hooks';
import { isPublisherFollowed } from 'client/respondent/core';
import { SubscriptionType } from 'graphql-schema/schema-types.g';
import { ShareModal } from 'client/shared/components/share-modal';

export const VotingHeaderActions: React.FC<{
  readonly onExitClicked: () => void;
  readonly publisher: Pick<PublishingEntity, 'name' | 'id'>;
  readonly respondent: Pick<CurrentRespondentData, 'id' | 'guest'> | null;
  readonly subscriptionType: SubscriptionType | null;
  readonly shareable: boolean;
  readonly shareUrl: string | null;
}> = (p) => {
  const [showUnfollowConfirm, setShowUnfollowConfirm] =
    React.useState<boolean>(false);
  const [showCreateAccountFollowPublisher, setShowCreateAccountFollowPublisher] =
    React.useState<boolean>(false);
  const [showShareModal, setShowShareModal] = React.useState<boolean>(false);

  const executeShareModalAction = (isOpen: boolean) => {
    setShowShareModal(isOpen);
  };
  const followMut = useMutationInfo(MutationInfos.followPublishingEntity, {});

  const followPublisher = async (respondentId: string | null, follow: boolean) => {
    if (!respondentId) {
      return;
    }

    return await followMut.fn({
      variables: {
        follow,
        publishingEntityId: p.publisher.id,
        respondentId,
      },
      refetchQueries: [
        QueryInfos.respondentSubscriptions.refetchInfo({
          respondentId,
        }),
      ],
    });
  };
  const isFollowing = isPublisherFollowed(p.subscriptionType);

  const executePublisherAction = async () => {
    if (p.respondent?.guest ?? true) {
      setShowCreateAccountFollowPublisher(true);
    } else if (isFollowing) {
      setShowUnfollowConfirm(true);
    } else {
      await followPublisher(p.respondent?.id ?? null, true);
    }
  };
  return (
    <>
      <Stack flexDirection={'row'} gap={1} justifyContent={'end'}>
        {p.shareable && !!p.shareUrl && (
          <Tooltip content={'Share'} id={'share-button-tooltip'}>
            <IconButton
              aria-label={'Share'}
              id={'share-content-button'}
              onClick={() => executeShareModalAction(true)}
            >
              <MaterialIcon icon={MaterialIconName.SHARE} />
            </IconButton>
          </Tooltip>
        )}

        <Tooltip
          content={`${isFollowing ? 'Unfollow' : 'Follow'} ${p.publisher.name}`}
          id={'follow-button-tooltip'}
        >
          <IconButton
            aria-label={'Follow publisher'}
            id={'follow-publisher-button'}
            onClick={() => executePublisherAction()}
          >
            <MaterialIcon
              icon={
                isFollowing
                  ? MaterialIconName.PERSON_REMOVE_ALT_1
                  : MaterialIconName.PERSON_ADD_ALT_1
              }
            />
          </IconButton>
        </Tooltip>
        <IconButton
          aria-label="Close"
          id={'close-voting-button'}
          onClick={() => p.onExitClicked()}
        >
          <MaterialIcon className="text-jungle" icon={MaterialIconName.CLOSE} />
        </IconButton>
      </Stack>
      <UnfollowConfirm
        events={{
          cancel: () => setShowUnfollowConfirm(false),
          unfollow: async () => {
            await followPublisher(p.respondent?.id ?? null, false);
            setShowUnfollowConfirm(false);
          },
        }}
        isOpen={showUnfollowConfirm}
        publisherName={p.publisher.name}
      />
      <FollowPublisherAccountPrompt
        isOpen={showCreateAccountFollowPublisher}
        onAuthenticationSuccess={async ({ respondentId }) => {
          await followPublisher(respondentId, true);
        }}
        onClose={() => setShowCreateAccountFollowPublisher(false)}
        publisherName={p.publisher.name}
      />
      {p.shareUrl && (
        <ShareModal
          events={{
            facebook: {
              share: () =>
                fbShare(window.location.href + '?iid=' + (p.respondent?.id ?? null)),
            },
            twitter: {
              share: () =>
                twitterShare(
                  window.location.href + '?iid=' + (p.respondent?.id ?? null)
                ),
            },
            cancel: () => setShowShareModal(false),
          }}
          isOpen={showShareModal}
          url={p.shareUrl}
        />
      )}
    </>
  );
};
VotingHeaderActions.displayName = 'VotingHeaderActions';
